import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntroOutroContext } from '../context/IntroOutroContext';
import { useTrimContext } from '../context/VideoTrimContex';
import Hls from 'hls.js';
import Queue from 'queue';
import ResourceHelper from '../utils/ResourceHelper';

/**
 *
 * This hook controls clip organizer player. The MultipleVideosPlayer component includes two HTML players that used one by one to provide users
 * with seamless playing experience. PLayers are rendered from MultipleVideosPlayer component and presented here as array of references - videoElements.
 * Initially rendered HTML players don't have any src, all video data, are loaded by this hook with help of hls.js library.
 *
 * To improve page performance for videos with big amount of scenes (especially on a machines with low productivity) only several loaded
 * hls are allowed in a one time. This a mount is limited by MAX_LOAD_SOURCES constant and could be easily changed. If all allowed hls instance 'spots' are
 * busy, the oldest attached scene (the latest played by user) will be replaced with a new one.
 *
 * Also hook check for a scene to be processed (in case of recording, rerecording scene) to add/replace a source when scene will be ready for playing.
 */

const MAX_PLAYER_ELEMENTS = 2;
const MAX_LOAD_SOURCES = 5;

const TIMEOUT_TO_LOAD_NEXT_COEFF = 0.3; // Load next scene if current scene left to 0.3 part of duration, and 0.7 is already played

const SCENE_STATE = {
  NONE: 0,
  EMPTY_SOURCE: 1,
  LOADED: 2,
  ATTACHED_TO_PLAYER: 3,
  PLAYING: 4,
  NEW_SOURCE: 5,
};

// Maintain players for smooth playback and better performance
export default function useMultiplyPlayer({ scenes, sceneIndex }) {
  const [handleNext, setHandleNext] = useState(false);
  const [seekPlayerTo, setSeekPlayerTo] = useState(null);
  const [currentPlayerKey, setCurrentPlayerKey] = useState('');
  const [init, setInit] = useState(false);
  const scenesShouldBeUpdatedRef = useRef([]);
  const scenesEnhancingRef = useRef([]);

  const reattachingSceneIds = useRef([]);
  const videoElements = useRef(null);
  const hlsInstances = useRef(null);
  const processingSceneId = useRef();

  const lastActionTimeout = useRef();

  const [previousScenesId, setPreviousScenesId] = useState(scenes?.map(item => item.id));

  const currentPlayerIndex = useMemo(() => videoElements.current?.findIndex(item => item.key === currentPlayerKey), [
    videoElements.current,
    currentPlayerKey,
  ]);

  // Invert currentPlayerIndex to find player index, that is not in used now
  const nextPlayerIndex = useMemo(() => (currentPlayerIndex ? 0 : 1), [currentPlayerIndex]);

  const { trimmingOn } = useTrimContext();
  const { blockPlayingIntroOutro } = useIntroOutroContext();

  const playerSourceQueue = new Queue({ results: [], autostart: true, concurrency: 1 });

  const getCurrentSceneState = scene => {
    let state = null;
    let playerIndex = null;
    let hlsIndex = null;

    if (hlsInstances.current && videoElements.current) {
      state = SCENE_STATE.NONE;
      let source = null;
      if (scene.id.toLowerCase() !== 'intro' && scene.id.toLowerCase() !== 'outro') {
        source = ResourceHelper.getResourceByLabel(scene, 'hls')?.url;
      } else {
        source = scene.video;
      }
      if (source) {
        playerIndex = videoElements.current.findIndex(item => item.sceneId === scene.id);
        hlsIndex = hlsInstances.current.findIndex(item => item.sceneId === scene.id);
        if (hlsIndex >= 0) {
          if (playerIndex >= 0) {
            const newSource =
              hlsInstances.current[hlsIndex].hls.url !== source || scenesShouldBeUpdatedRef.current.find(item => item === scene.id);
            if (newSource) {
              state = SCENE_STATE.NEW_SOURCE;
            } else {
              state = currentPlayerIndex === playerIndex ? SCENE_STATE.PLAYING : SCENE_STATE.ATTACHED_TO_PLAYER;
            }
          } else {
            state = SCENE_STATE.LOADED;
          }
        } else {
          state = SCENE_STATE.NONE;
        }
      } else {
        state = SCENE_STATE.EMPTY_SOURCE;
      }
      return {
        state,
        playerIndex,
        hlsIndex,
      };
    }
    return null;
  };

  // handle scenes and sceneIndex change for player
  useEffect(() => {
    if (sceneIndex < 0) {
      return;
    }

    if (scenes?.length && hlsInstances.current && videoElements.current) {
      const currentPlayerScene = scenes.find(({ id }) => id === videoElements.current[currentPlayerIndex].sceneId);
      if (currentPlayerScene) {
        const scenesIdToUpdate = structuredClone(scenesShouldBeUpdatedRef.current);
        const scenesEnhancingClone = structuredClone(scenesEnhancingRef.current);
        // Check processing scenes, put them into waiting for new source scenes id array
        scenes.forEach(scene => {
          if (scene.processing && !scenesShouldBeUpdatedRef.current.find(id => id === currentPlayerScene.id)) {
            scenesIdToUpdate.push(scene.id);
          }
          const isSceneEnhancing = scenesEnhancingRef.current.find(({ sceneId }) => sceneId === scene.id) !== undefined;
          if (scene.enhancing && !isSceneEnhancing) {
            scenesEnhancingClone.push({ sceneId: scene.id, done: false });
          }
          if (scene.enhancingDone && isSceneEnhancing) {
            scenesEnhancingClone.forEach(item => {
              if (item.sceneId === scene.id) item.done = true
            })
          }
          if (!scene.enhancing && !scene.enhancingDone && isSceneEnhancing) {
            scenesEnhancingClone.forEach(item => {
              if (item.sceneId === scene.id) item.done = true
            })
          }
        });
        scenesShouldBeUpdatedRef.current = scenesIdToUpdate;
        scenesEnhancingRef.current = scenesEnhancingClone;
        if (!currentPlayerScene.processing && !currentPlayerScene.enhancing) {
          checkSourceInPlayer(currentPlayerScene, currentPlayerIndex);
        }
      }

      const checkSceneState = () => {
        const { state, playerIndex, hlsIndex } = getCurrentSceneState(scenes[sceneIndex]);

        switch (state) {
          case SCENE_STATE.NONE:
            playerSourceQueue.push(async () => {
              if (videoElements?.current.length < MAX_PLAYER_ELEMENTS && scenes.length > 1) {
                await addSecondPlayer(scenes[sceneIndex].id);
              }
              await loadAndAttach(scenes[sceneIndex], currentPlayerKey);
              switchPlayers(scenes[sceneIndex], sceneIndex);
            });
            break;

          case SCENE_STATE.LOADED:
            playerSourceQueue.push(async () => {
              const nextPlayerIndex = videoElements.current.findIndex(item => item.key !== currentPlayerKey);
              await reattachVideo(hlsIndex, nextPlayerIndex, scenes[sceneIndex]);
              switchPlayers(scenes[sceneIndex], sceneIndex);
            });
            break;

          case SCENE_STATE.NEW_SOURCE:
            playerSourceQueue.push(async () => {
              await checkSourceInPlayer(scenes[sceneIndex], playerIndex);
              switchPlayers(scenes[sceneIndex], sceneIndex);
            });
            break;

          case SCENE_STATE.ATTACHED_TO_PLAYER:
            playerSourceQueue.push(async () => {
              switchPlayers(scenes[sceneIndex], sceneIndex);
            });

            break;

          case SCENE_STATE.EMPTY_SOURCE:
            playerSourceQueue.push(async () => {
              if (videoElements?.current.length < MAX_PLAYER_ELEMENTS && scenes.length > 1) {
                await addSecondPlayer(scenes[sceneIndex].id);
              }
              let nextPlayerIndex = playerIndex;
              if (playerIndex === null || playerIndex < 0) {
                nextPlayerIndex = await loadAndAttach(scenes[sceneIndex], currentPlayerKey);
              }
              videoElements.current[nextPlayerIndex].sceneId = scenes[sceneIndex].id;

              setCurrentPlayerKey(videoElements.current[nextPlayerIndex].key);
              videoElements.current.forEach((item, index) => {
                if (index !== nextPlayerIndex) {
                  item.controller.abort();
                  item.controller = new AbortController();
                  setHandleNext(false);
                }
              });
            });
            break;

          default:
            break;
        }
      };

      // Check if scenes are the same. If yes - that means that sceneIndex changed, and useEffect can run without timeout
      // If scenes changed - add timeout in case, sceneIndex will change, which cause useEffect to call again. Pre prio always
      // last change (sceneIndex or scenes)

      let isScenesArrayChange = scenes.length !== previousScenesId.length;
      if (!isScenesArrayChange) {
        isScenesArrayChange = scenes
          .map(item => item.id)
          .every(function (value, index) {
            return value === previousScenesId[index];
          });
        setPreviousScenesId(structuredClone(scenes.map(item => item.id)));
      }

      // Clear timeout is current action appear before the timeout ends
      if (lastActionTimeout.current) {
        clearTimeout(lastActionTimeout.current);
        lastActionTimeout.current = null;
      }

      // Add timeout if scenes updated
      if (isScenesArrayChange) {
        lastActionTimeout.current = setTimeout(async () => {
          checkSceneState();
          lastActionTimeout.current = null;
        }, 15);
      } else {
        checkSceneState();
      }
    }
  }, [scenes, sceneIndex]);

  const checkNextScene = (sceneId, sceneSource = null) => {
    let state = SCENE_STATE.NONE;
    let playerIndex = null;
    let hlsIndex = null;
    // Check if scene already in one of the player
    const playerWithSceneIndex = videoElements.current.findIndex(item => item.sceneId === sceneId);
    // Check if scene hls was loaded
    const loadedInstanceIndex = hlsInstances.current.findIndex(item => item.sceneId === sceneId);
    if (playerWithSceneIndex >= 0) {
      playerIndex = playerWithSceneIndex;
      // Check if hls source remain same

      if (loadedInstanceIndex >= 0 && (!sceneSource || sceneSource === hlsInstances.current[loadedInstanceIndex].hls.url)) {
        state = SCENE_STATE.ATTACHED_TO_PLAYER;
      } else {
        state = SCENE_STATE.NEW_SOURCE;
      }
      hlsIndex = loadedInstanceIndex;
    } else {
      if (loadedInstanceIndex >= 0) {
        // Only for intro/outro, cause only these resources can have different source
        if (!sceneSource || sceneSource === hlsInstances.current[loadedInstanceIndex].hls.url) {
          state = SCENE_STATE.LOADED;
        } else {
          state = SCENE_STATE.NEW_SOURCE;
        }
        hlsIndex = loadedInstanceIndex;
      }
    }
    return {
      state,
      playerIndex,
      hlsIndex,
    };
  };

  // Listener for player to check the player progress
  const checkProgress = scene => {
    return function curried_func(event) {
      if (scene.trimEnd / 1000 - event.target.currentTime <= ((scene.trimEnd - scene.trimStart) / 1000) * TIMEOUT_TO_LOAD_NEXT_COEFF) {
        setHandleNext(true);
      }
    };
  };

  /**
   * Get next available scene (scene is not a draft or not processing)
   * @returns index
   */
  const getNextAvailableIndex = (scenes, sceneIndex = null) => {
    let nextAvailableIndex = 0;
    const allScenesDraft = scenes.every(item => item.video === null);
    if (sceneIndex < scenes.length - 1) {
      nextAvailableIndex = sceneIndex + 1;
    }
    if (scenes[nextAvailableIndex].video === null && !allScenesDraft) {
      const nextScenes = scenes.slice(nextAvailableIndex + 1).filter(item => item.video !== null);
      nextAvailableIndex = nextScenes.length
        ? nextScenes.findIndex(item => item.video !== null)
        : scenes.findIndex(item => item.video !== null);
    }
    if (scenes.length > nextAvailableIndex) {
      if (blockPlayingIntroOutro && scenes[nextAvailableIndex].id === 'Outro') {
        if (allScenesDraft) {
          nextAvailableIndex = 0;
        } else {
          const nextRecordedScene = Array.from(scenes)
            .filter(item => item.id !== 'Intro')
            .find(item => item.video);
          nextAvailableIndex = nextRecordedScene ? scenes.indexOf(nextRecordedScene) : scenes.length;
        }
      }
    }
    return nextAvailableIndex;
  };

  /**
   * Creates new hls instance
   * @returns created hls instance
   */
  const createNewHlsInstance = (startPosition = 0, sceneId) => {
    const hls = new Hls({ startPosition });
    hls.on(Hls.Events.MEDIA_DETACHED, function () {
      console.log(sceneId, 'media DETACHED');
    });
    hls.on(Hls.Events.DESTROYING, function () {
      console.log(sceneId, 'DESTROYING');
    });
    hls.on(Hls.Events.MANIFEST_PARSED, function () {
      console.log(sceneId, 'media MANIFEST_PARSED');
    });
    hls.on(Hls.Events.MEDIA_ATTACHED, function () {
      console.log(sceneId, 'video and hls.js are now bound together !');
    });
    hls.on(Hls.Events.ERROR, function (event, data) {
      if (data.fatal) {
        switch (data.type) {
          case Hls.ErrorTypes.MEDIA_ERROR:
            console.log('fatal media error encountered, try to recover', data);
            hls.recoverMediaError();
            break;
          case Hls.ErrorTypes.NETWORK_ERROR:
            console.error('fatal network error encountered', data);
            // All retries and media options have been exhausted.
            // Immediately trying to restart loading could cause loop loading.
            // Consider modifying loading policies to best fit your asset and network
            // conditions (manifestLoadPolicy, playlistLoadPolicy, fragLoadPolicy).
            break;
          default:
            // cannot recover
            hls.destroy();
            break;
        }
      } else if (data.details === 'internalException' && data.type === 'otherError' && isMobile()) {
        const level = last(this.getLevels());

        hls.removeLevel(level?.level, level?.urlId);

        hls.startLoad();
      }
    });
    return hls;
  };

  /**
   * Checks hls instances array to define if there is available stop to create an additional instance (array length is less then MAX_LOAD_SOURCES),
   * or there is an empty instance (after some scene was removed). In other case, checks instance array to find the oldest attached instance to replace it
   * @returns Return next available hls instance index
   */
  const gelHlsInstanceIndex = (startTime, sceneId) => {
    let hlsIndex = 0;
    // If hls instances length is not more then MAX load sources value - just create an instance and push in to sources array
    if (hlsInstances.current.length < MAX_LOAD_SOURCES) {
      const hls = createNewHlsInstance(startTime, sceneId);
      hlsInstances.current.push({ hls });
      hlsIndex = hlsInstances.current.length - 1;
    } else {
      // Check if source array has empty sources items, that could be reused
      const availableInstanceIndex = hlsInstances.current.findIndex(item => item.hls === null);
      if (availableInstanceIndex >= 0) {
        hlsIndex = availableInstanceIndex;
      } else {
        // Find last attached source to reuse it
        const latestTimestamp = Math.min(...hlsInstances.current.map(item => item?.lastAttached ?? 0));
        hlsIndex = hlsInstances.current.findIndex(item => !item?.lastAttached || item.lastAttached === latestTimestamp);
        // Destroy the oldest hls source, clear the additional data
        hlsInstances.current[hlsIndex].hls.destroy();
        hlsInstances.current[hlsIndex].hls = null;
        videoElements.current.forEach(item => {
          if (item.sceneId === hlsInstances.current[hlsIndex].sceneId) {
            item.sceneId = null;
          }
        });
        hlsInstances.current[hlsIndex].sceneId = null;
      }
      // Create new hls instance and attach it to source array
      const newHls = createNewHlsInstance(startTime, sceneId);
      hlsInstances.current[hlsIndex].hls = newHls;
    }
    return hlsIndex;
  };

  /**
   * This function replaces data from oldest scene in array with a new, that should be played. hls.js library
   * does not allow to use one instance with different sources.
   * Because of that, previous hls instance should be destroyed, and another hls instance is created
   * @param {*} hlsInstanceIndex hls instance index that should be replaced
   * @param {*} source source url for video
   */
  const replaceSource = async (hlsInstanceIndex, source, startPosition, sceneId) => {
    // Compare hls instance url with current scene url, destroy and replace hls instance with updated source
    if (hlsInstances.current[hlsInstanceIndex]?.hls?.media) {
      await hlsInstances.current[hlsInstanceIndex].hls.destroy();
      hlsInstances.current[hlsInstanceIndex].hls = null;
    }

    const hls = createNewHlsInstance(startPosition, sceneId);
    hlsInstances.current[hlsInstanceIndex].sceneId = sceneId;
    hlsInstances.current[hlsInstanceIndex].hls = hls;
    await hlsInstances.current[hlsInstanceIndex].hls.loadSource(source);
  };

  /**
   * Check if the player scene source should be reloaded
   * @param {*} scene scene data to check
   * @param {*} playerIndex scene player index
   */
  const checkSourceInPlayer = async (scene, playerIndex) => {
    processingSceneId.current = scene.id;
    const source = ResourceHelper.getResourceByLabel(scene, 'hls')?.url ?? scene.video;
    if (source) {
      const hlsInstanceIndex = hlsInstances.current.findIndex(item => item?.sceneId === scene.id);
      const isUpdateAfterEnhancing = scenesEnhancingRef.current.find(({ sceneId, done }) => sceneId === scene.id && done) !== undefined;
      const isUpdatedAfterProcessing = scenesShouldBeUpdatedRef.current.find(item => item === scene.id) !== undefined;
      const hasDifferentSource =
        hlsInstanceIndex >= 0 &&
        source &&
        (isUpdatedAfterProcessing ||
          hlsInstances.current[hlsInstanceIndex].hls.url !== source || isUpdateAfterEnhancing);
      if (hasDifferentSource) {
        if (isUpdatedAfterProcessing) {
          scenesShouldBeUpdatedRef.current = scenesShouldBeUpdatedRef.current.filter(id => id !== scene.id);
        }
        if (isUpdateAfterEnhancing) {
          scenesEnhancingRef.current = scenesEnhancingRef.current.filter(({ sceneId }) => sceneId !== scene.id);
        }
        const fixCurrentTime = videoElements.current[playerIndex].video.currentTime;
        await replaceSource(hlsInstanceIndex, source, scene.trimStart / 1000, scene.id);
        await hlsInstances.current[hlsInstanceIndex].hls.attachMedia(videoElements.current[playerIndex].video);
        videoElements.current[playerIndex].video.currentTime = fixCurrentTime;

        processingSceneId.current = null;
      }
    }
  };

  /**
   * Function detaches previous video from the player and attached a new one
   * @param {*} newHlsIndex hls instance that should be attached
   * @param {*} playerIndex player for next hls instance
   * @param {*} source source url of the scene. Is optional, and should not be null only in case video is not loaded yet
   */
  const reattachVideo = async (newHlsIndex, playerIndex, scene, source = null) => {
    if (reattachingSceneIds?.current?.find(sceneId => sceneId === scene.id)) {
      return;
    } else {
      reattachingSceneIds?.current.push(scene.id);
    }

    // Find index of hls instance that was played before in this player
    const previousHlsIndex = hlsInstances.current.findIndex(item => item?.sceneId === videoElements.current[playerIndex]?.sceneId);
    if (previousHlsIndex >= 0) {
      if (hlsInstances.current[previousHlsIndex].hls.media) {
        // Detach previous hls from the player
        await detachVideo(previousHlsIndex);
      }
    }
    const shouldBeUpdated = scenesShouldBeUpdatedRef.current.find(item => item === scene.id) !== undefined;
    // Check if the scene is waiting for new source to be loaded and if yes replace the source
    if (shouldBeUpdated) {
      await replaceSource(newHlsIndex, source, scene.trimStart / 1000, scene.id);
      scenesShouldBeUpdatedRef.current = scenesShouldBeUpdatedRef.current.filter(id => id !== scene.id);
    }
    if (videoElements.current[playerIndex]?.video && !hlsInstances.current[newHlsIndex].hls.media) {
      await hlsInstances.current[newHlsIndex].hls.attachMedia(videoElements.current[playerIndex].video);
    }
    if (source?.length > 0 && (!shouldBeUpdated || !hlsInstances.current[newHlsIndex].hls.url)) {
      await hlsInstances.current[newHlsIndex].hls.loadSource(source);
      hlsInstances.current[newHlsIndex].sceneId = scene.id;
    } else {
      hlsInstances.current[newHlsIndex].hls.on(Hls.Events.BUFFER_CREATED, () => {
        hlsInstances.current[newHlsIndex].hls.startLoad((scene.trimStart ?? 0) / 1000);
      });
    }
    // Updated lastAttached timestamp for hls instance
    hlsInstances.current[newHlsIndex].lastAttached = Date.now();
    if (videoElements.current[playerIndex].video?.readyState !== HTMLMediaElement.HAVE_NOTHING) {
      videoElements.current[playerIndex].video.currentTime = scene.trimStart / 1000;
    } else {
      // Add listener to seek to in the beginning of the video when video will be available
      // console.log('seekPlayerTo', seekPlayerTo, scenes, scene.id);
      videoElements.current[playerIndex].video.onloadedmetadata = () => {
        if (seekPlayerTo && scenes[seekPlayerTo?.newVideoIndex].id === scene.id) {
          videoElements.current[playerIndex].video.currentTime = seekPlayerTo.value;
          setSeekPlayerTo(null);
        } else {
          const newTime = scene?.trimStart / 1000;
          videoElements.current[playerIndex].video.currentTime = !isNaN(newTime) && isFinite(newTime) ? newTime : 0;
        }
      };
    }
    videoElements.current[playerIndex].sceneId = scene.id;
    reattachingSceneIds.current = [...reattachingSceneIds.current].filter(sceneId => sceneId !== scene.id);
  };

  const detachVideo = async hlsInstanceIndex => {
    await hlsInstances.current[hlsInstanceIndex].hls.stopLoad();
    await hlsInstances.current[hlsInstanceIndex].hls.detachMedia();
  };

  const addSecondPlayer = sceneId => {
    console.log('will addSecondPlayer');
    return new Promise(resolve => {
      const key = `multiply-player-${videoElements?.current.length}`;
      const controller = new AbortController();
      videoElements?.current.push({
        sceneId: sceneId,
        key,
        controller,
      });
      const findSecondPlayerInterval = setInterval(() => {
        const container = document.getElementById(key);
        if (container) {
          const video = container.querySelector('video');
          videoElements.current[1].video = video;
          clearInterval(findSecondPlayerInterval);
          resolve();
        }
      }, 100);
    });
  };

  /**
   * Function loads the scene source and attach it to the next available player
   * @param {*} scenes current scenes
   * @param {*} index scene index that should be loaded to the player
   * @returns player index with attached source
   */
  const loadAndAttach = useCallback(
    async (scene, currentPlayerKey) => {
      // Find player index that is not in use
      let playerIndex = videoElements?.current?.findIndex(item => item.sceneId === scene.id);
      if (playerIndex < 0) {
        playerIndex = videoElements?.current?.findIndex(item => item.key !== currentPlayerKey);
      }
      // Check if the scene has Hls
      const source = ResourceHelper.getResourceByLabel(scene, 'hls')?.url ?? scene?.video;
      // If scene is Draft or Processing
      if (scene.isDraft || scene.processing || !source) {
        // Find unused player hls and detach the source from it, so player will have no src, as scene is a draft
        hlsInstances.current?.forEach(async (item, index) => {
          if (item?.sceneId === videoElements.current[playerIndex]?.sceneId) {
            await detachVideo(index);
          }
        });
      } else {
        // Find available instance index to attach and load a new source
        let oldestHlsIndex = gelHlsInstanceIndex(scene.trimStart / 1000, scene.id);
        await reattachVideo(oldestHlsIndex, playerIndex, scene, source);
      }
      return playerIndex;
    },
    [reattachVideo, gelHlsInstanceIndex]
  );

  /**
   * Switch players, remove timeupdate listener from previous, add timeupdate listener for next.
   * Set player currentTime to seek value os scene start depending on seekPlayerTo state
   * @param {*} sceneIndex
   */
  const switchPlayers = (scene, sceneIndex) => {
    videoElements.current.forEach((item, index) => {
      if (item.sceneId !== scene.id) {
        item.controller.abort();
        item.controller = new AbortController();
        setHandleNext(false);
      } else {
        if (seekPlayerTo?.newVideoIndex === sceneIndex) {
          setTimeout(() => {
            item.video.currentTime = seekPlayerTo.value;
          }, 10);
          setSeekPlayerTo(null);
        } else {
          if (item.video) {
            item.video.currentTime = scene.trimStart / 1000;
          }
        }
        setCurrentPlayerKey(item.key);
        // Add listener only if video exists
        const source = ResourceHelper.getResourceByLabel(scene, 'hls')?.url ?? scene.video;
        if (source) {
          item.video?.addEventListener('timeupdate', checkProgress(scene), { signal: item.controller.signal });
          playerSourceQueue.push(async () => {
            setTimeout(() => {
              if (
                videoElements.current[index].sceneId === scene.id &&
                videoElements.current[index].video.readyState === HTMLMediaElement.HAVE_NOTHING
              ) {
                console.log(videoElements.current[index].sceneId, 'Player has nothing, will try to recover');
                const sceneInPlayer = scenes.find(item => item.id === videoElements.current[index].sceneId);
                const source = ResourceHelper.getResourceByLabel(sceneInPlayer, 'hls')?.url ?? sceneInPlayer.video;
                hlsInstances.current.forEach(async hlsInstance => {
                  if (hlsInstance.sceneId === sceneInPlayer.id) {
                    await hlsInstance.hls.stopLoad();
                    await hlsInstance.hls.detachMedia();
                    await hlsInstance.hls.attachMedia(videoElements.current[index].video);
                    await hlsInstance.hls.loadSource(source);
                    hlsInstance.hls.on(Hls.Events.BUFFER_CREATED, () => {
                      console.log(
                        `reattachVideo ${sceneInPlayer.id}\n`,
                        'BUFFER_CREATED will startLoad from',
                        (sceneInPlayer.trimStart ?? 0) / 1000
                      );
                      hlsInstance.hls.startLoad((sceneInPlayer.trimStart ?? 0) / 1000);
                    });
                  }
                });
              }
            }, 1000);
          });
        }
      }
    });
  };

  // Init the hls instances array, load sources, create video elements array, attach it with first scenes
  useEffect(() => {
    if (scenes?.length && !init) {
      const scenesWithSource = scenes.filter(
        item => (ResourceHelper.getResourceByLabel(item, 'hls') ?? item.video) && !item.processing
      );
      const scenesForHlsLoad =
        scenesWithSource.length > MAX_LOAD_SOURCES ? scenesWithSource.slice(0, MAX_LOAD_SOURCES) : structuredClone(scenesWithSource);
      hlsInstances.current = scenesForHlsLoad.map(item => {
        if (item.enhancing) {
          scenesEnhancingRef.current.push({ sceneId: item.id, done: false });
        }
        const hls = createNewHlsInstance(item.trimStart / 1000, item.id);
        const hlsResource = ResourceHelper.getResourceByLabel(item, 'hls')?.url ?? item.video;
        hlsResource && hls.loadSource(hlsResource);
        hls.startLoad(item.trimStart / 1000);
        hls.on(Hls.Events.MEDIA_ATTACHED, function () {
          console.log(item.id, 'video and hls.js are now bound together !');
        });
        return { sceneId: item.id, hls };
      });

      const scenesToPlayer = scenes.length > MAX_PLAYER_ELEMENTS ? scenes.slice(0, MAX_PLAYER_ELEMENTS) : structuredClone(scenes);

      videoElements.current = scenesToPlayer.slice(0, MAX_PLAYER_ELEMENTS).map((scene, index) => {
        const controller = new AbortController();
        return {
          sceneId: scene.id,
          key: `multiply-player-${index}`,
          controller,
        };
      });
      setCurrentPlayerKey(videoElements.current[0].key);

      setInit(true);
    }
  }, [scenes]);

  // Clean up. Destroy all hls instances on unmount
  useEffect(() => {
    return () => hlsInstances?.current?.forEach(item => item?.hls?.destroy());
  }, []);

  // Attach rendered HTML video elements to VideoElements array
  useEffect(() => {
    const findElementsOnPage = () => {
      const allReady = videoElements.current
        .map(item => {
          const container = document.getElementById(item.key);
          if (container) {
            const video = container.querySelector('video');
            if (document.getElementById(item.key)) {
              return { key: item.key, video };
            }
            return null;
          }
          return null;
        })
        .filter(item => item !== null);
      if (allReady.length !== videoElements.current.length) {
        setTimeout(() => {
          findElementsOnPage();
        }, 250);
      }

      allReady.forEach((item, playerIndex) => {
        const index = videoElements.current.findIndex(ref => item.key === ref.key);
        if (index >= 0) {
          // videoElements now has ref to player HTML element
          videoElements.current[index].video = item.video;
          // Attach hls with player
          const hlsIndex = hlsInstances.current.findIndex(item => item.sceneId === videoElements.current[index].sceneId);
          if (hlsIndex >= 0) {
            hlsInstances.current[hlsIndex].hls.attachMedia(item.video);
            hlsInstances.current[hlsIndex].lastAttached = Date.now() + playerIndex;
          }
        }
      });

      videoElements?.current?.forEach(item => {
        if (item.sceneId === scenes[sceneIndex].id) {
          setCurrentPlayerKey(item.key);
          setTimeout(() => {
            item.video.currentTime = scenes[sceneIndex].trimStart / 1000;
          }, 100);
          // Add listener only if video exists
          const source = ResourceHelper.getResourceByLabel(scenes[sceneIndex], 'hls')?.url ?? scenes[sceneIndex].video;
          if (source) {
            item.video.addEventListener('timeupdate', checkProgress(scenes[sceneIndex]), { signal: item.controller.signal });
          }
        } else {
          item.controller.abort();
          item.controller = new AbortController();
        }
      });

      return;
    };

    videoElements?.current?.length && findElementsOnPage();
  }, [videoElements.current]);

  // Check if next scene hls is already loaded, start loading hls, attach scene to unused player
  useEffect(() => {
    if (handleNext && !trimmingOn) {
      if (reattachingSceneIds.current.find(sceneId => sceneId === scenes[sceneIndex].id) !== undefined) {
        return;
      }
      // Find next scene to prepare for seamless playing
      const nextSceneIndex = getNextAvailableIndex(scenes, sceneIndex);

      const { state, playerIndex, hlsIndex } = checkNextScene(scenes[nextSceneIndex]?.id, scenes[nextSceneIndex]?.video);

      switch (state) {
        case SCENE_STATE.NONE:
          playerSourceQueue.push(async () => {
            if (videoElements?.current.length < MAX_PLAYER_ELEMENTS && scenes.length > 1) {
              await addSecondPlayer(scenes[nextSceneIndex].id);
            }
            await loadAndAttach(scenes[nextSceneIndex], currentPlayerKey);
            videoElements.current[playerIndex].sceneId = scenes[nextSceneIndex]?.id;
          });
          break;

        case SCENE_STATE.LOADED:
          // Attach to a player that is not in use, but do not switch yet
          playerSourceQueue.push(async () => {
            await reattachVideo(hlsIndex, nextPlayerIndex, scenes[nextSceneIndex]);
          });
          break;

        case SCENE_STATE.ATTACHED_TO_PLAYER:
          playerSourceQueue.push(async () => {
            scenes[nextSceneIndex].video &&
              processingSceneId.current === null &&
              (await checkSourceInPlayer(scenes[nextSceneIndex], playerIndex));
          });

        default:
          break;
      }
    }
  }, [handleNext]);

  // Check seeking for current played scene
  useEffect(() => {
    if (seekPlayerTo && sceneIndex === seekPlayerTo?.newVideoIndex) {
      videoElements.current.forEach(item => {
        if (item.key === currentPlayerKey) {
          if (item.video.readyState !== HTMLMediaElement.HAVE_NOTHING) {
            item.video.currentTime = seekPlayerTo.value;
            setSeekPlayerTo(null);
          } else {
            item.video.onloadedmetadata = () => {
              item.video.currentTime = seekPlayerTo.value;
              setSeekPlayerTo(null);
            };
          }
          item.video.currentTime = seekPlayerTo.value;
        }
      });
    }
  }, [seekPlayerTo]);

  return {
    videoElements: videoElements?.current,
    setSeekPlayerTo,
    currentPlayerKey,
  };
}
